import { Component, Inject, Injector, ViewChild, computed, signal, inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { DatePipe } from "@angular/common";
import { Router } from "@angular/router";

import { GlossInputComponent } from "@bitwarden/web-vault/app/components/inputs/gloss-input/gloss-input.component";
import { BalanceForm } from "@bitwarden/web-vault/app/gloss/manage/manage-accounts/account-balance/balance-form";
import { AccountType, dialogDataType } from "@bitwarden/web-vault/app/models/types/account.types";
import { isBankOrCredit } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { TransactionService } from "@bitwarden/web-vault/app/services/DataService/transaction/transaction.service";
import { InputTypes } from "@bitwarden/web-vault/app/models/enum/inputTypes.enum";
import { GuideWizardService } from "@vault/web-vault/app/services/guideWizard/guide-wizard.service";
import {
  GuideTitleEnum,
  ManualAccountGuideSteps,
  ManualAccountGuideTotalSteps,
} from "@bitwarden/web-vault/app/models/enum/guide.enum";
import { GlobalService } from "@bitwarden/common/services/global/global.service";

const getLabelsByAccountType = (type: AccountType, formatter: (s: string) => string) => {
  const labels = {
    currency: formatter("currency"),
    balance: formatter("balance"),
  };

  switch (type) {
    case "stock":
      labels.currency = formatter("symbol");
      labels.balance = formatter("amount");
      break;
    case "other":
      labels.currency = `${formatter("currency")} / ${formatter("symbol")}`;
      labels.balance = formatter("amount");
      break;
  }

  return labels;
};

@Component({
  selector: "app-account-balance",
  templateUrl: "./account-balance.component.html",
})
export class AccountBalanceComponent {
  /** True while the balance is being saved; the panel won't close on click-away/Escape. */
  protected submitting = false;
  private transactionService = inject(TransactionService);
  private datePipe = inject(DatePipe);
  private globalService = inject(GlobalService);
  private readonly dialogRef = inject(MatDialogRef<AccountBalanceComponent>);
  protected router: Router = inject(Router);
  protected guideWizardService: GuideWizardService = inject(GuideWizardService);

  protected readonly InputTypes = InputTypes;
  protected readonly ManualAccountGuideSteps = ManualAccountGuideSteps;
  protected readonly ManualAccountGuideTotalSteps = ManualAccountGuideTotalSteps;
  protected dialogFactory = new DialogFactory();
  protected currencyFieldLabel = "";
  protected balanceFieldLabel = "";
  protected dateText = "";

  // Restrict the date field to today or earlier.
  protected dateFilter = (d: Date | null): boolean => !!d && d <= new Date();

  balanceForm: BalanceForm;
  data: dialogDataType;
  bankOrCredit = signal(true);
  symbolOptions = computed(() => this.transactionService.getSelectableBalanceSymbols());

  constructor(
    private injector: Injector,
    @Inject(MAT_DIALOG_DATA)
    data: dialogDataType,
  ) {
    this.data = data;
    this.balanceForm = new BalanceForm(this.injector, this.data, this.submit.bind(this));
    const accountType = this.data.accountView.type;
    const isBankOrCreditAccount = isBankOrCredit(accountType);

    if (isBankOrCreditAccount !== this.bankOrCredit()) {
      this.bankOrCredit.set(isBankOrCreditAccount);
    }

    // todo fix this
    const labels = getLabelsByAccountType(accountType, (s) => this.balanceForm.i18nService.t(s));
    this.currencyFieldLabel = labels.currency;
    this.balanceFieldLabel = labels.balance;

    // Use initialDate if provided, otherwise default to yesterday
    this.dateText = this.data.initialDate ?? this.getYesterdayDateText();
  }

  protected getYesterdayDateText(): string {
    const d = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return this.datePipe.transform(d, "yyyy-MM-dd")!;
  }

  onDatePicked(date: Date) {
    this.dateText = this.datePipe.transform(date, "yyyy-MM-dd")!;
    this.balanceForm.onDateChange(this.dateText);
  }

  openAddSymbolDialog() {
    this.dialogFactory.addSymbol((symbol) => this.balanceForm.selectCurrency(symbol.code));
  }

  async submit() {
    // this.balanceInput.inputValueControl.markAsTouched();
    // this.balanceInput.onInputBlur();

    if (this.balanceForm.formGroup.valid) {
      this.submitting = true;
      try {
        await this.balanceForm.submit();
      } catch {
        this.globalService.showErrorMessage("errorOccurred", "unexpectedError");
        return;
      } finally {
        this.submitting = false;
      }
      if (this.data.isFromGuide) {
        try {
          await this.guideWizardService.completeTask(
            this.guideWizardService.getTaskByGuideTitle(GuideTitleEnum.addManualAccount),
          );
        } catch {
          this.globalService.showErrorMessage("errorOccurred", "unexpectedError");
          return;
        }
        this.dialogRef.close();
        this.data.actionSucceeded();
        this.dialogFactory.addTransaction(
          this.data.accountView,
          () => this.router.navigate(["/guide"]),
          true,
        );
      } else {
        this.data.actionSucceeded();
      }
    } else {
      this.balanceForm.formGroup.markAllAsTouched();
      this.balanceForm.resetForm();
    }
  }
}
